import { Injectable, OnDestroy, Provider } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import {
  BehaviorSubject,
  first,
  Observable,
  ReplaySubject,
  startWith,
  Subject,
  Subscription,
} from 'rxjs';
import { FooterButton } from '../../../reusable-components/footer/footer.service';
import { map } from 'rxjs/operators';

export function provideMatStepperService(currentStep = 0): Provider {
  return {
    provide: MatStepperService,
    useFactory: () => {
      const index = new Subject<number>();
      const matStepper = {
        selectedIndexChange: index.asObservable(),
        selectedIndex: currentStep,
        previous: () => {
          const newIndex = Math.max(matStepper.selectedIndex - 1, 0);
          matStepper.selectedIndex = newIndex;
          index.next(newIndex);
        },
        next: () => {
          const newIndex = Math.min(matStepper.selectedIndex + 1, 2);
          matStepper.selectedIndex = newIndex;
          index.next(newIndex);
        },
        steps: {
          get: (i: number) => ({
            editable: i !== 1,
          }),
        },
      } as unknown as MatStepper;
      const matStepperService = new MatStepperService();
      matStepperService.setMatStepper(matStepper);
      return matStepperService;
    },
  };
}

@Injectable()
export class MatStepperService implements OnDestroy {
  private matStepper = new ReplaySubject<MatStepper>(1);
  private matStepper$ = this.matStepper.asObservable();

  private currentStepSubscription: Subscription | undefined;
  private disableRoomSubscription: Subscription | undefined;

  private currentStep: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly currentStep$: Observable<number> =
    this.currentStep.asObservable();

  private previousRoomStepState = '';
  // Will set the icon in the stepper to https://fonts.google.com/icons?selected=Material+Symbols+Outlined:block:FILL@0;wght@400;GRAD@0;opsz@24
  private readonly DISABLED_STATE = 'block';

  public setMatStepper(matStepper: MatStepper) {
    this.matStepper.next(matStepper);
    this.currentStepSubscription?.unsubscribe();
    this.currentStep.next(matStepper.selectedIndex);
    this.currentStepSubscription = matStepper.selectedIndexChange.subscribe(
      (index: number) => {
        this.currentStep.next(index);
      },
    );
  }

  public next(): void {
    this.matStepper$
      .pipe(first())
      .subscribe((matStepper: MatStepper) => matStepper.next());
  }

  public previous(): void {
    this.matStepper$
      .pipe(first())
      .subscribe((matStepper: MatStepper) => matStepper.previous());
  }

  public getPreviousButton(): FooterButton {
    return {
      label: 'components.footer.prev',
      click: () => {
        this.previous();
      },
      disabled: this.getPreviousDisabled(),
    };
  }

  public getNextButton(): FooterButton {
    return {
      label: 'components.footer.next',
      click: () => {
        this.next();
      },
      disabled: this.getNextDisabled(),
    };
  }

  public disableBackToRooms(disable = true): void {
    this.disableRoomSubscription?.unsubscribe();
    this.disableRoomSubscription = this.matStepper$.subscribe(
      (matStepper: MatStepper) => {
        const roomsStep = matStepper.steps.get(1);
        if (roomsStep) {
          if (roomsStep.state !== this.DISABLED_STATE) {
            this.previousRoomStepState = roomsStep.state;
          }
          roomsStep.editable = !disable;
          if (disable) {
            roomsStep.state = this.DISABLED_STATE;
          } else {
            roomsStep.state = this.previousRoomStepState;
          }
        }
      },
    );
  }

  private getPreviousDisabled(): Observable<boolean> {
    return this.matStepper$
      .pipe(
        map(
          (matStepper: MatStepper) =>
            !matStepper.steps.get(matStepper.selectedIndex - 1)?.editable,
        ),
      )
      .pipe(startWith(false));
  }

  private getNextDisabled(): Observable<boolean> {
    return this.matStepper$
      .pipe(
        map((matStepper: MatStepper) => {
          return !matStepper.steps.get(matStepper.selectedIndex + 1)?.editable;
        }),
      )
      .pipe(startWith(false));
  }

  ngOnDestroy(): void {
    this.currentStepSubscription?.unsubscribe();
    this.disableRoomSubscription?.unsubscribe();
  }
}
